import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import { useApi } from "@/hooks/useApi";
import * as z from "zod";
import { toast } from "sonner";
import type { AxiosError } from "axios";

// The backend signals MCP failures in a few shapes:
//   - OAuth discovery/registration failures return `{ error: "OAuth setup
//     failed: ..." }` with a 422. Some of those (known provider missing DCR
//     support) also carry `code` and `provider`; `code` is currently always
//     `"no_dcr"`.
//   - Business-rule guards use Laravel `abort(422, "...")`, which puts the text
//     under `message` (e.g. "Maximum of 100 MCP servers per organization").
//   - Field validation (`$request->validate`) returns `{ message: "The given
//     data was invalid.", errors: { field: ["..."] } }`; the per-field text is
//     more useful than the generic top-level `message`.
type McpApiErrorBody = {
  error?: string;
  message?: string;
  errors?: Record<string, string[]>;
  code?: string;
  provider?: string;
};

export interface McpApiError {
  message: string;
  code?: string;
  provider?: string;
}

export function getMcpApiError(error: unknown, fallback: string): McpApiError {
  const data = (error as AxiosError<McpApiErrorBody>)?.response?.data;
  const firstValidationError = data?.errors
    ? Object.values(data.errors)
        .flat()
        .find((m): m is string => typeof m === "string" && m.length > 0)
    : undefined;
  // Prefer an explicit `error` (OAuth failures), then a specific field
  // validation message, then a top-level `abort()` message, then the fallback.
  return {
    message: data?.error ?? firstValidationError ?? data?.message ?? fallback,
    code: data?.code,
    provider: data?.provider,
  };
}

export function getMcpApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  return getMcpApiError(error, fallback).message;
}

const StdioEnvValueSchema = z.object({
  value: z.string(),
  sensitive: z.boolean(),
});

const McpLastFailureSchema = z.object({
  code: z.string(),
  message: z.string(),
  action: z.string().nullable(),
  // Sanitized failure detail (fixed templates plus a scrubbed stdio stderr
  // tail). Only present for users who can manage the server.
  detail: z.string().nullable().optional(),
  failed_at: z.string().nullable().optional(),
});

const McpLastToolErrorSchema = z.object({
  code: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  action: z.string().nullable().optional(),
  detail: z.string().nullable().optional(),
  failed_at: z.string().nullable().optional(),
});

const McpLearnedSummarySchema = z.object({
  tool_count: z.number(),
  updated_at: z.string(),
});

const McpServerSchema = z.object({
  transport: z.enum(["http", "stdio"]).optional().default("http"),
  url: z.string().optional().default(""),
  stdio_command: z.string().nullable().optional(),
  stdio_args: z.array(z.string()).optional().default([]),
  stdio_env_keys: z.array(z.string()).optional().default([]),
  stdio_env: z.record(z.string(), StdioEnvValueSchema).optional().default({}),
  enabled: z.boolean().optional().default(true),
  display_name: z.string().optional(),
  provider_id: z.string().nullable().optional(),
  oauth_docs_url: z.string().nullable().optional(),
  header_docs_url: z.string().nullable().optional(),
  stdio_docs_url: z.string().nullable().optional(),
  scope: z.enum(["team", "personal"]),
  auth_type: z.string().optional().default("header"),
  auth_status: z.string().optional().default("pending"),
  // OAuth account identity (OIDC claims) captured when the connection was
  // authorized; disambiguates whose provider account the tokens belong to.
  oauth_account: z
    .object({
      email: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      username: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  auth_header_keys: z.array(z.string()).optional().default([]),
  can_manage: z.boolean().optional().default(false),
  created_by: z
    .object({
      id: z.number(),
      name: z.string().nullable().optional(),
      username: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  last_updated_by: z
    .object({
      id: z.number(),
      name: z.string().nullable().optional(),
      username: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  created_at: z.string().nullable().optional(),
  last_updated_at: z.string().nullable().optional(),
  last_failure: McpLastFailureSchema.nullable().optional(),
  last_tool_error: McpLastToolErrorSchema.nullable().optional(),
  learned: McpLearnedSummarySchema.optional(),
});

// Same PHP quirk at the top level: empty associative array → []
const mcpServersMapSchema = z.preprocess(
  (val) => (Array.isArray(val) && val.length === 0 ? {} : val),
  z.record(z.string(), McpServerSchema),
);

const McpServersResponseSchema = z.object({
  mcp_servers: mcpServersMapSchema,
});

export type McpServer = z.infer<typeof McpServerSchema>;
export type McpServersResponse = z.infer<typeof McpServersResponseSchema>;

const McpKnowledgeResponseSchema = z.object({
  tools: z.array(
    z.object({
      tool_name: z.string(),
      input_schema_hash: z.string(),
      updated_at: z.string(),
      fragments: z.array(
        z.object({
          fragment_hash: z.string(),
          fragment: z.record(z.string(), z.unknown()),
        }),
      ),
    }),
  ),
  truncated: z.boolean(),
});

export type McpKnowledgeResponse = z.infer<typeof McpKnowledgeResponseSchema>;

export interface UpsertMcpServerInput {
  name?: string;
  display_name: string;
  url: string;
  transport?: "http" | "stdio";
  stdio_command?: string | null;
  stdio_args?: string[];
  stdio_env?: Record<string, { value: string; sensitive: boolean }>;
  scope: "team" | "personal";
  provider_id?: string | null;
  enabled?: boolean;
  auth_type?: "header" | "oauth" | "none";
  auth_headers?: Record<string, string>;
}

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that already have the active org from another source (e.g.
 * the URL pathname) and need that same org to drive the MCP server fetch.
 * Shares the cache key with `useMcpServers` so the two stay in sync once the
 * URL org and active org agree. Mirrors the
 * `useCodesmithSettingsForOrg`/`useCodesmithSettings` pattern.
 */
export function useMcpServersForOrg(org: string | null | undefined) {
  const api = useApi();

  return useQuery(
    ["mcp-servers", org],
    async () => {
      const response = await api.get(
        `/user/github/orgs/${org}/codesmith-settings/mcp-servers`,
      );
      return McpServersResponseSchema.parse(response.data);
    },
    {
      enabled: !!org,
      // OAuth flows finish in another window; refetching on focus lets any
      // surface showing statuses self-heal even when the callback page's
      // postMessage never reaches us (e.g. a provider's COOP severed
      // window.opener).
      refetchOnWindowFocus: true,
    },
  );
}

// Origin of the backend API — the MCP OAuth callback HTML is served from it.
const API_ORIGIN = process.env.NEXT_PUBLIC_API_URL
  ? new URL(process.env.NEXT_PUBLIC_API_URL).origin
  : "";

/**
 * Refreshes every cached MCP server list when an MCP OAuth callback page
 * reports its result via postMessage. Mounted once at the app root so the
 * pending → connected transition lands even after the surface that opened
 * the popup unmounted or its bounded polling window already closed.
 */
export function useMcpOAuthResultListener(): void {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== API_ORIGIN) return;
      if (
        (event.data as { type?: unknown } | null)?.type !== "mcp-oauth-result"
      ) {
        return;
      }
      void queryClient.invalidateQueries(["mcp-servers"]);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [queryClient]);
}

export function useMcpServers() {
  const { activeGithubOrg } = useActiveGithubOrgForUser();
  return useMcpServersForOrg(activeGithubOrg);
}

export function useMcpKnowledge(
  org: string | null | undefined,
  serverName: string | null,
  enabled: boolean,
) {
  const api = useApi();

  return useQuery(
    ["mcp-knowledge", org, serverName],
    async () => {
      const response = await api.get(
        `/user/github/orgs/${org}/codesmith-settings/mcp-servers/${encodeURIComponent(
          serverName ?? "",
        )}/knowledge`,
      );
      return McpKnowledgeResponseSchema.parse(response.data);
    },
    {
      enabled: !!org && !!serverName && enabled,
    },
  );
}

/**
 * Refreshes every cached MCP server list when a turn finishes.
 *
 * Registration and tool-call failures are recorded while the agent works,
 * and nothing else refetches while the session view stays mounted — so
 * without this the chat surfaces keep rendering the status from page load
 * (typically "Connected") while the settings page, which refetches on
 * mount, shows the failure and its Re-authorize CTA. Invalidating the whole
 * prefix covers the session's org and the dashboard's active org, which the
 * chat surfaces resolve independently.
 */
export function useRefreshMcpServersAfterTurn(isTurnActive: boolean): void {
  const queryClient = useQueryClient();
  const wasTurnActive = React.useRef(isTurnActive);

  React.useEffect(() => {
    const justFinished = wasTurnActive.current && !isTurnActive;
    wasTurnActive.current = isTurnActive;
    if (!justFinished) return;
    void queryClient.invalidateQueries(["mcp-servers"]);
  }, [isTurnActive, queryClient]);
}

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that resolve their org from another source (e.g. the
 * session's installation) so the save hits that org's MCP config. Shares
 * the cache key with `useMcpServersForOrg` for the same org.
 */
export function useUpsertMcpServerForOrg(org: string | null | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (input: UpsertMcpServerInput) => {
      const response = await api.put(
        `/user/github/orgs/${org}/codesmith-settings/mcp-servers`,
        input,
      );
      return McpServersResponseSchema.parse(response.data);
    },
    {
      onError: (error) => {
        toast.error(getMcpApiErrorMessage(error, "Failed to save MCP server"));
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["mcp-servers", org], data);
      },
    },
  );
}

export function useUpsertMcpServer() {
  const { activeGithubOrg } = useActiveGithubOrgForUser();
  return useUpsertMcpServerForOrg(activeGithubOrg);
}

export function useDeleteMcpServer() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  return useMutation(
    async (serverName: string) => {
      const response = await api.delete(
        `/user/github/orgs/${activeGithubOrg}/codesmith-settings/mcp-servers/${serverName}`,
      );
      return McpServersResponseSchema.parse(response.data);
    },
    {
      onError: () => {
        toast.error("Failed to delete MCP server");
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["mcp-servers", activeGithubOrg], data);
        toast.success("MCP server deleted");
      },
    },
  );
}

export interface ToggleMcpServerInput {
  serverName: string;
  /** Desired state for the server, i.e. the state the switch was moved to. */
  enabled: boolean;
}

type ToggleQueueEntry = {
  serverName: string;
  inFlight: boolean;
  hasPendingIntent: boolean;
  pendingEnabled: boolean;
  latestIntentId: number;
  initialRollbackEnabled?: boolean;
  persistedEnabled?: boolean;
};

const TOGGLE_MCP_SERVER_MUTATION_KEY = ["toggle-mcp-server"];

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that resolve their org from another source (e.g. the
 * session's installation) so the toggle hits that org's MCP config. Shares
 * the cache key with `useMcpServersForOrg` for the same org.
 */
export function useToggleMcpServerForOrg(org: string | null | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  const toggleQueuesRef = React.useRef(new Map<string, ToggleQueueEntry>());
  const pendingInvalidationRef = React.useRef(false);

  const queryKey = ["mcp-servers", org];

  const setCachedServer = (
    serverName: string,
    updater: (server: McpServer) => McpServer,
  ) => {
    const currentSettings =
      queryClient.getQueryData<McpServersResponse>(queryKey);
    const currentServer = currentSettings?.mcp_servers?.[serverName];

    if (!currentSettings || !currentServer) {
      return undefined;
    }

    queryClient.setQueryData<McpServersResponse>(queryKey, {
      ...currentSettings,
      mcp_servers: {
        ...currentSettings.mcp_servers,
        [serverName]: updater(currentServer),
      },
    });

    return currentServer;
  };

  const mutation = useMutation(
    async ({ serverName, enabled }: ToggleMcpServerInput) => {
      const response = await api.patch(
        `/user/github/orgs/${org}/codesmith-settings/mcp-servers/${serverName}/toggle`,
        // Send the target state so the request is idempotent; the backend
        // sets it directly instead of blindly flipping the stored value.
        { enabled },
      );
      return McpServersResponseSchema.parse(response.data);
    },
    {
      mutationKey: TOGGLE_MCP_SERVER_MUTATION_KEY,
    },
  );

  const mergeServerResponse = (
    serverName: string,
    response: McpServersResponse,
  ) => {
    const server = response.mcp_servers[serverName];
    if (!server) return;

    const merged = setCachedServer(serverName, () => server);
    if (!merged) {
      queryClient.setQueryData(queryKey, response);
    }
  };

  const invalidateWhenQueuesAreIdle = () => {
    if (toggleQueuesRef.current.size > 0) {
      pendingInvalidationRef.current = true;
      return;
    }

    pendingInvalidationRef.current = false;
    void queryClient.invalidateQueries(queryKey);
  };

  const processQueue = async (queueKey: string) => {
    const entry = toggleQueuesRef.current.get(queueKey);
    if (!entry || entry.inFlight) return;

    entry.inFlight = true;

    while (entry.hasPendingIntent) {
      const enabled = entry.pendingEnabled;
      const intentId = entry.latestIntentId;
      entry.hasPendingIntent = false;

      try {
        const data = await mutation.mutateAsync({
          serverName: entry.serverName,
          enabled,
        });
        entry.persistedEnabled = data.mcp_servers[entry.serverName]?.enabled;

        if (!entry.hasPendingIntent && entry.latestIntentId === intentId) {
          mergeServerResponse(entry.serverName, data);
        }
      } catch {
        if (!entry.hasPendingIntent && entry.latestIntentId === intentId) {
          const rollbackEnabled =
            entry.persistedEnabled ?? entry.initialRollbackEnabled;

          if (rollbackEnabled !== undefined) {
            setCachedServer(entry.serverName, (server) => ({
              ...server,
              enabled: rollbackEnabled,
            }));
          }

          pendingInvalidationRef.current = true;
          toast.error("Failed to toggle MCP server");
        }
      }
    }

    entry.inFlight = false;
    if (!entry.hasPendingIntent) {
      toggleQueuesRef.current.delete(queueKey);
      if (pendingInvalidationRef.current) {
        invalidateWhenQueuesAreIdle();
      }
    } else {
      void processQueue(queueKey);
    }
  };

  const enqueueToggle = async ({
    serverName,
    enabled,
  }: ToggleMcpServerInput) => {
    await queryClient.cancelQueries(queryKey);

    const queueKey = `${org}:${serverName}`;
    let entry = toggleQueuesRef.current.get(queueKey);
    const existingServer = setCachedServer(serverName, (server) => ({
      ...server,
      enabled,
    }));

    if (!entry) {
      entry = {
        serverName,
        inFlight: false,
        hasPendingIntent: false,
        pendingEnabled: enabled,
        latestIntentId: 0,
        initialRollbackEnabled: existingServer?.enabled,
      };
      toggleQueuesRef.current.set(queueKey, entry);
    }

    entry.latestIntentId += 1;
    entry.pendingEnabled = enabled;
    entry.hasPendingIntent = true;

    void processQueue(queueKey);
  };

  return {
    ...mutation,
    mutate: (input: ToggleMcpServerInput) => {
      void enqueueToggle(input);
    },
    mutateAsync: enqueueToggle,
  };
}

export function useToggleMcpServer() {
  const { activeGithubOrg } = useActiveGithubOrgForUser();
  return useToggleMcpServerForOrg(activeGithubOrg);
}

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that resolve their org from another source (e.g. the
 * session's installation) so the OAuth flow targets that org's server.
 */
export function useAuthorizeMcpServerForOrg(org: string | null | undefined) {
  const api = useApi();

  return useMutation(
    async (input: {
      serverName: string;
      popup: Window | null;
      /**
       * Session the re-authorize was launched from (the session-thread
       * re-auth card). The backend enqueues a continuation prompt into it
       * once the OAuth flow lands connected, so the agent resumes without
       * the user typing anything.
       */
      sessionId?: string;
      /**
       * Callers that let the user cancel or restart an OAuth attempt while
       * the authorize request is in flight pass this to report whether the
       * attempt was abandoned; when it returns true the post-success
       * navigation and the failure toast are skipped so a late settlement
       * can't reopen an OAuth tab (or surface an error) for an attempt the
       * user already gave up on.
       */
      isAbandoned?: () => boolean;
    }) => {
      const response = await api.post(
        `/user/github/orgs/${org}/codesmith-settings/mcp-servers/${input.serverName}/authorize`,
        input.sessionId ? { assistant_session_id: input.sessionId } : undefined,
      );
      return {
        ...(response.data as { authorize_url: string }),
        popup: input.popup,
      };
    },
    {
      onError: (error, variables) => {
        // Close the blank popup if the API call failed
        variables.popup?.close();
        // An abandoned attempt's card has already moved on (canceled or
        // restarted), so surfacing its failure would be noise.
        if (variables.isAbandoned?.()) return;
        toast.error(
          getMcpApiErrorMessage(error, "Failed to start OAuth authorization"),
        );
      },
      onSuccess: (data, variables) => {
        if (variables.isAbandoned?.()) return;
        if (data.popup && !data.popup.closed) {
          data.popup.location.href = data.authorize_url;
        } else {
          // Popup was blocked or closed — fall back to direct navigation
          window.open(data.authorize_url, "_blank");
        }
      },
    },
  );
}

export function useAuthorizeMcpServer() {
  const { activeGithubOrg } = useActiveGithubOrgForUser();
  return useAuthorizeMcpServerForOrg(activeGithubOrg);
}

const McpToolSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  // Raw MCP inputSchema for the client-side draft 2020-12 compatibility
  // check; null when missing or too large for the handshake payload.
  input_schema: z.unknown().nullable().optional(),
});

export type McpTool = z.infer<typeof McpToolSchema>;

// Result from POST /codesmith-settings/mcp-servers/test. The endpoint always
// returns 200 with this shape; check `ok` for handshake success. `tools` is
// null when the server's tools couldn't be enumerated (null means "unknown",
// not "no tools").
const McpTestResultSchema = z.object({
  ok: z.boolean(),
  http_status: z.number().nullable(),
  server_info: z
    .object({
      name: z.string().nullable(),
      version: z.string().nullable(),
      protocol_version: z.string().nullable(),
    })
    .nullable(),
  tools: z.array(McpToolSchema).nullable().optional(),
  error: z.string().nullable(),
});

export type McpTestResult = z.infer<typeof McpTestResultSchema>;

export type TestMcpServerInput =
  | {
      transport?: "http";
      url: string;
      auth_type: "header" | "oauth";
      auth_headers?: Record<string, string>;
      // When provided, the backend uses the saved server's stored credentials
      // (lets users re-test an existing server with its real auth).
      name?: string;
    }
  | {
      transport: "stdio";
      stdio_command: string;
      stdio_args: string[];
      // Names the key set to test: non-blank values override, blank values
      // fall back to the saved server's stored env (when `name` matches a
      // saved stdio server with the same command line), and stored keys
      // absent from the map are excluded.
      stdio_env?: Record<string, string>;
      name?: string;
    };

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`; see
 * `useUpsertMcpServerForOrg` for when to use it.
 */
export function useTestMcpServerForOrg(org: string | null | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (input: TestMcpServerInput) => {
      const response = await api.post(
        `/user/github/orgs/${org}/codesmith-settings/mcp-servers/test`,
        input,
      );
      return McpTestResultSchema.parse(response.data);
    },
    {
      onSuccess: (data, input) => {
        // Successful named stdio probes clear ERROR on the saved row; refresh
        // the list so Retry / dialog Test drop the failure CTA without a reload.
        if (data.ok && input.transport === "stdio" && input.name) {
          void queryClient.invalidateQueries(["mcp-servers", org]);
        }
      },
    },
  );
}

export function useTestMcpServer() {
  const { activeGithubOrg } = useActiveGithubOrgForUser();
  return useTestMcpServerForOrg(activeGithubOrg);
}

export function useDisconnectMcpServer() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  return useMutation(
    async (serverName: string) => {
      const response = await api.post(
        `/user/github/orgs/${activeGithubOrg}/codesmith-settings/mcp-servers/${serverName}/disconnect`,
      );
      return McpServersResponseSchema.parse(response.data);
    },
    {
      onError: () => {
        toast.error("Failed to disconnect MCP server");
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["mcp-servers", activeGithubOrg], data);
        toast.success("MCP server disconnected");
      },
    },
  );
}
